import React, { useEffect, useMemo, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Icon, Input } from '@node-space/ui-components'
import { useRoutingNumberValidation } from 'hooks/queries/Beneficiaries/useRoutingNumberValidation'
import { useBicValidation } from 'hooks/queries/useBicValidation'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useIbanValidation } from 'hooks/queries/useIbanValidation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useBankDetailValidation } from 'pages/Contacts/hooks/useBankDetailsValidation'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, FieldsFetchedFromIbanValidation } from 'pages/Contacts/types/fiatInstruments'
import { resetBankAddress } from 'pages/Contacts/utils/fiatInstruments'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { isIbanFormat } from 'utils/beneficiaries'
import { AddressDetails } from './AddressDetails'

type InternationalBankDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBeneficiaryAddress?: boolean
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  formFieldLabels: {
    accountNumber: string
    accountNumberPlaceholder: string
    bankCode: string
    bankCodePlaceholder: string
  }
  contactBeneficiary?: BeneficiaryDetails
}

export const InternationalBankDetails = ({
  formValues,
  errors,
  clearErrors,
  register,
  setValue,
  trigger,
  setError,
  formFieldLabels,
  contactBeneficiary,
}: InternationalBankDetailsProps) => {
  const { t } = useTranslation()
  const { setRequestError } = useMappedErrorMessage()

  const [isValidBic, setIsValidBic] = useState<boolean>(false)
  const [isValidIban, setIsValidIban] = useState<boolean>(false)
  const [isValidRoutingNumber, setIsValidRoutingNumber] = useState<boolean>(false)

  const { data: countries, isPending: countriesLoading } = useCountriesQuery(true)
  const {
    refetch: validateIban,
    isFetching: isValidatingIban,
    isError: isValidatingIbanError,
    data: ibanBankDetails,
  } = useIbanValidation(formValues?.accountNumber, 'IBAN', setRequestError, false)
  const {
    refetch: validateBic,
    isFetching: isValidatingBic,
    isError: isValidatingBicError,
    data: bicValidationData,
    error: bicValidationError,
  } = useBicValidation(formValues?.bankCode, setRequestError, false)
  const {
    refetch: validateRoutingNumber,
    isFetching: isValidatingRoutingNumber,
    isError: isValidatingRoutingNumberError,
    data: routingNumberBankDetails,
  } = useRoutingNumberValidation(formValues?.routingNumber, setRequestError, false)

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isInternationalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isCountryUs = formValues?.bankCountryCode === 'US'
  const isRoutingNumberVisible = isUsd && isInternationalBankAccount && isCountryUs

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, clearErrors])

  const isIban = isIbanFormat(formValues?.accountNumber)

  const updateFieldValue = (field: keyof FieldsFetchedFromIbanValidation, value: string) => {
    setValue(field, value)
  }

  const setIbanBankDetailsData = (details: FieldsFetchedFromIbanValidation) => {
    if (Object.keys(details || {})?.length) {
      Object.keys(details)?.forEach(detail => {
        if (detail === 'bankCountry') {
          onInternationalCountrySelection(details[detail])
        } else {
          updateFieldValue(detail as keyof FieldsFetchedFromIbanValidation, details[detail])
        }
      })
    }
  }

  useBankDetailValidation({
    formValues,
    setValue,
    setError,
    clearErrors,
    ibanValidationProps: {
      isValidatingIbanError,
      ibanBankDetails,
      setIsValidIban,
      setIbanBankDetailsData: setIbanBankDetailsData,
    },
    bicValidationProps: {
      isValidatingBicError,
      bicValidationData,
      bicValidationError,
      setIsValidBic,
    },
    routingNumberValidationProps: {
      isValidatingRoutingNumberError,
      routingNumberBankDetails,
      setIsRoutingNumberValid: setIsValidRoutingNumber,
    },
    contactBeneficiary,
    isIbanFormat: isIban,
    isValidIban,
  })

  const onInternationalFieldUpdate =
    (fieldName: keyof ContactFiatInstrumentFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(fieldName, e?.target?.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const handleAccountNumberOnBlur = async () => {
    if (contactBeneficiary) return
    if (!formValues?.accountNumber || !isIbanFormat) return

    const isIbanValid = await trigger('accountNumber')
    if (!isIbanValid) return

    validateIban()
  }

  const handleInternationalBankCodeOnBlur = async () => {
    if (contactBeneficiary) return
    // if USD and
    if (isIbanFormat) return

    if (!formValues?.bankCode) return

    clearErrors()

    validateBic()
  }

  const internationalCountryList = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }

    return []
  }, [countries])

  const handleInternationalRoutingValidation = async () => {
    if (contactBeneficiary) return
    if (!formValues?.routingNumber) return
    if (formValues?.currency !== BeneficiaryDetailsCurrency.USD) return

    validateRoutingNumber()
  }

  useEffect(() => {
    if (isValidatingRoutingNumberError && !formValues?.routingNumber) {
      resetBankAddress(setValue, 'bank')
    }
  }, [formValues?.routingNumber, isValidatingRoutingNumberError])

  useEffect(() => {
    if (isValidatingRoutingNumberError && formValues?.routingNumber) {
      setError('routingNumber', { message: t('abaRoutingNumberError') })
    }
  }, [isValidatingRoutingNumberError, formValues?.routingNumber, setError, t])

  const onInternationalCountrySelection = (countryCode: string) => {
    const countryName = internationalCountryList.find(
      country => country?.value === countryCode
    )?.leftText
    setValue('bankCountryName', countryName)
    setValue('bankCountryCode', countryCode)
  }

  const isValidBankCode = useMemo(() => {
    if (isIbanFormat) {
      return isValidIban
    }

    if (formValues?.currency === BeneficiaryDetailsCurrency.USD && !isIbanFormat) {
      return isValidBic
    }

    return false
  }, [isValidIban, isValidBic, formValues?.currency])

  const { accountNumber, bankCode, bankCountryCode } = formValues || {}

  return (
    !!formValues?.currency && (
      <Box width="full" paddingT={12} gap={12}>
        <h2 className="text-foreground-prominent text-h2">{t('formFields.bankDetails')}</h2>

        {isUsd && (
          <Box paddingT={12}>
            <Combobox
              {...register('bankCountryCode')}
              key={formValues?.bankCountryCode}
              label={t('country')}
              id={t('bankCountryCode')}
              selectPlaceholder={t('searchOrSelectACountry')}
              items={internationalCountryList}
              onChange={onInternationalCountrySelection}
              loading={countriesLoading}
              defaultValue={formValues?.bankCountryCode}
              errorText={errors?.bankCountryCode?.message}
            />
          </Box>
        )}

        {(!isUsd || (isUsd && bankCountryCode)) && (
          <>
            <Box flex gapX={12} paddingT={12}>
              <Box flexSize="auto" width="full">
                <Input
                  {...register('accountNumber')}
                  name="accountNumber"
                  testId="accountNumber"
                  label={formFieldLabels?.accountNumber}
                  placeholder={formFieldLabels?.accountNumberPlaceholder}
                  errorText={errors?.accountNumber?.message}
                  error={!!errors?.accountNumber?.message}
                  onBlur={handleAccountNumberOnBlur}
                  onChange={onInternationalFieldUpdate('accountNumber')}
                  loading={isValidatingIban}
                  IconButton={
                    isIbanFormat &&
                    isValidIban &&
                    accountNumber && <Button variant="ghost" iconElement={<Icon name="Check" />} />
                  }
                  iconPosition="right"
                />
              </Box>
              <Box flexSize="auto" width="full">
                <Input
                  {...register('bankCode')}
                  name="bankCode"
                  testId="bankCode"
                  label={formFieldLabels.bankCode}
                  placeholder={formFieldLabels?.bankCodePlaceholder}
                  errorText={errors?.bankCode?.message}
                  error={!!errors?.bankCode?.message}
                  onBlur={handleInternationalBankCodeOnBlur}
                  loading={isValidatingBic}
                  IconButton={
                    isValidBankCode &&
                    bankCode && <Button variant="ghost" iconElement={<Icon name="Check" />} />
                  }
                  onChange={onInternationalFieldUpdate('bankCode')}
                  iconPosition="right"
                />
              </Box>
            </Box>

            {isRoutingNumberVisible && (
              <Box paddingT={12}>
                <Input
                  {...register('routingNumber')}
                  testId="routingNumber"
                  label={t('formFields.routingNumber')}
                  placeholder={t('formFields.placeholder.routingNumber')}
                  errorText={errors?.routingNumber?.message}
                  error={!!errors?.routingNumber?.message}
                  loading={isValidatingRoutingNumber}
                  onChange={onInternationalFieldUpdate('routingNumber')}
                  onBlur={handleInternationalRoutingValidation}
                  value={formValues?.routingNumber}
                  IconButton={
                    isValidRoutingNumber &&
                    formValues?.routingNumber && (
                      <Button variant="ghost" iconElement={<Icon name="Check" />} />
                    )
                  }
                  iconPosition="right"
                />
              </Box>
            )}

            <Box paddingY={12}>
              <Input
                {...register('bankName')}
                name="bankName"
                testId="bankName"
                label={t('beneficiarysBankName')}
                placeholder={t('enterBankName')}
                errorText={errors?.bankName?.message}
                error={!!errors?.bankName?.message}
                onChange={onInternationalFieldUpdate('bankName')}
              />
            </Box>

            <AddressDetails
              formValues={formValues}
              errors={errors}
              type={AddressTypes.BANK}
              clearErrors={clearErrors}
              register={register}
              setError={setError}
              setValue={setValue}
            />
          </>
        )}

        {!isUsd && (
          <Box paddingT={12}>
            <Combobox
              {...register('bankCountryCode')}
              key={formValues?.bankCountryCode}
              label={t('country')}
              id={t('bankCountryCode')}
              selectPlaceholder={t('searchOrSelectACountry')}
              items={internationalCountryList}
              onChange={onInternationalCountrySelection}
              loading={countriesLoading}
              defaultValue={formValues?.bankCountryCode}
              errorText={errors?.bankCountryCode?.message}
            />
          </Box>
        )}
      </Box>
    )
  )
}
